import { ShieldCheck, AlertTriangle } from "lucide-react";

/**
 * "Report artifact" — Luminous Atrium styling.
 * Left: feedback quote presented as a sentiment report.
 * Right: signal intelligence stats + privacy assurance panel.
 */
export const EditorialArtifact = () => {
  const stats: { label: string; value: string; accent?: boolean }[] = [
    { label: "Internal Trust Score", value: "68%" },
    { label: "Psychological Safety", value: "41%" },
    { label: "Manager Confidence", value: "57%" },
    { label: "Burnout Index", value: "72%" },
    { label: "Retention Risk", value: "High", accent: true },
  ];

  return (
    <section className="relative px-4 sm:px-6 md:px-10 pb-16 sm:pb-20 md:pb-32 overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-0 opacity-60"
        style={{
          background:
            "radial-gradient(800px 460px at 10% 0%, hsl(150 30% 90% / 0.5), transparent 60%), radial-gradient(700px 420px at 100% 100%, hsl(50 60% 92% / 0.45), transparent 60%)",
        }}
      />

      <div className="max-w-7xl mx-auto relative">
        <div className="flex items-center justify-center mb-8 md:mb-10">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/70 backdrop-blur px-3.5 py-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium text-foreground/70 shadow-[0_2px_8px_-4px_rgba(21,34,27,0.06)]">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            Sample Sentiment Report
          </span>
        </div>

        <div className="grid grid-cols-12 gap-5 md:gap-6">
          {/* Left: the quote */}
          <div className="col-span-12 lg:col-span-7 rounded-3xl bg-card border border-border/50 p-5 sm:p-10 md:p-14 shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)] transition-all duration-300 hover:-translate-y-1">
            <div className="flex justify-between items-center mb-6 sm:mb-10 md:mb-12 gap-3 flex-wrap">
              <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1 text-[11px] font-medium text-foreground/70">
                Submission #482
              </span>
              <span className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] font-mono text-muted-foreground tracking-wide">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" strokeWidth={2} />
                Anonymous &middot; Encrypted
              </span>
            </div>
            <blockquote className="font-sans font-medium text-xl sm:text-3xl md:text-4xl leading-[1.3] sm:leading-[1.25] tracking-[-0.02em] text-foreground mb-7 md:mb-10">
              &ldquo;There is a growing gap between our stated values and the daily reality of project prioritization.{" "}
              <span className="font-serif italic font-light text-foreground/75">
                It feels like we are trading long-term integrity for short-term optics
              </span>
              , and the team is starting to notice.&rdquo;
            </blockquote>
            <div className="flex items-center gap-3 pt-6 border-t border-border/60">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-destructive/10 text-destructive flex-shrink-0">
                <AlertTriangle className="h-[16px] w-[16px]" strokeWidth={1.75} />
              </span>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
                  Sentiment Analysis
                </span>
                <span className="text-sm font-medium text-foreground tracking-[-0.01em]">
                  Critical Structural Friction
                </span>
              </div>
            </div>
          </div>

          {/* Right: stats + privacy panel */}
          <div className="col-span-12 lg:col-span-5 flex flex-col gap-5 md:gap-6">
            <div className="rounded-3xl bg-card border border-border/50 p-6 sm:p-8 md:p-10 shadow-[0_8px_24px_-12px_rgba(21,34,27,0.06)] flex-1">
              <h3 className="text-xs uppercase tracking-[0.22em] font-medium text-muted-foreground mb-6">
                Signal Intelligence
              </h3>
              <div className="space-y-4">
                {stats.map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between items-baseline gap-3 border-b border-border/60 pb-3 last:border-b-0 last:pb-0"
                  >
                    <span className="font-sans text-sm text-foreground/80">
                      {row.label}
                    </span>
                    <span
                      className={`font-sans text-2xl md:text-[28px] font-medium tracking-[-0.02em] tabular-nums ${
                        row.accent ? "text-destructive" : "text-foreground"
                      }`}
                    >
                      {row.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl bg-foreground text-background p-6 sm:p-8 md:p-10 shadow-[0_24px_60px_-24px_rgba(21,34,27,0.4)]">
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="h-4 w-4 text-background/70" strokeWidth={1.75} />
                <span className="text-[10px] uppercase tracking-[0.22em] font-medium text-background/70">
                  Privacy Guarantee
                </span>
              </div>
              <p className="text-sm md:text-base font-sans leading-relaxed text-background/85">
                Captured via decentralized encryption protocols.{" "}
                <span className="font-serif italic font-light text-background/95">
                  The identity of the contributor is mathematically impossible to reconstruct.
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
